import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Badge, Button, Center, Spinner } from 'src/components/atoms';
import { BadgeVariant } from 'src/components/atoms/Badge/Badge.styles';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import {
  FormInput,
  handleErrorToast,
  handleSuccessToast,
  Modal,
  Table,
} from 'src/components/molecules';
import { DefaultOption } from 'src/components/molecules/DropdownSelect/components';
import { getFormattedDate } from 'src/components/pages/Cases/helpers/formatDate';
import { CaseLinkType, useCreateCaseLinkMutation } from 'src/graphql';
import { useCaseLinkSearch } from 'src/hooks/cases/useCaseLinkSearch';
import { FormInputVariant, FormState, getCaseReadableId } from 'src/types';
import {
  DetailLabel,
  FormContainer,
  LinkedCaseItemContainer,
  LoaderOverlay,
  StyledEmptyStateContainer,
  StyledRowContent,
  StyledTableRow,
  TableContainer,
} from './LinkCaseModal.styles';

const linkTypeOptions: DefaultOption[] = Object.entries(CaseLinkType).map(
  ([key, value]) => ({
    label: key.replace(/([A-Z])/g, ' $1').trim(),
    value,
  }),
);

const linkTypeDescriptions: Record<CaseLinkType, string> = {
  [CaseLinkType.Duplicate]: 'Identical cases submitted more than once.',
  [CaseLinkType.Related]:
    'Cases that are connected but do not fit another specific category.',
  [CaseLinkType.SeriesOverTime]:
    'A recurring pattern of similar incidents or misconduct (e.g. ongoing harassment, repeated fraud attempts).',
  [CaseLinkType.SameLocation]:
    'Multiple cases that occurred at the same site, facility, or within the same country.',
  [CaseLinkType.SameOffender]: 'Multiple cases involving the same individual.',
  [CaseLinkType.SameIncident]: 'Two or more cases related to the same event.',
};

interface Props {
  caseId: string;
  readableId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  refetchCaseLinks: () => void;
}

export const LinkCaseModal = ({
  caseId,
  readableId,
  open,
  setOpen,
  refetchCaseLinks,
}: Props) => {
  const queryClient = useQueryClient();

  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [linkType, setLinkType] = useState<DefaultOption | null>(null);
  const [notes, setNotes] = useState('');
  const [showValidationError, setShowValidationError] = useState(false);
  const [linkingLoading, setLinkingLoading] = useState(false);

  const {
    searchValue,
    setSearchValue,
    displayedResults,
    showLoader,
    hasSearchQuery,
  } = useCaseLinkSearch({
    caseId,
    enabled: open,
  });

  const resultCount = displayedResults?.count ?? 0;
  const columns = useMemo(
    () => [
      {
        label: `Search results (${resultCount})`,
        value: 'caseId',
      },
    ],
    [resultCount],
  );

  const tableData = useMemo(
    () =>
      (displayedResults?.items ?? []).map((c) => ({
        cellData: [
          <StyledTableRow key={c.id}>
            <StyledRowContent>
              {c.readableId}
              <DetailLabel>{c.name || 'Untitled'}</DetailLabel>
            </StyledRowContent>
            <StyledRowContent>
              Last Modified
              <DetailLabel>{getFormattedDate(c.updatedAt)}</DetailLabel>
            </StyledRowContent>
            <StyledRowContent>
              <Badge
                variant={c.status?.toLowerCase() as BadgeVariant}
                text={
                  c.status
                    ? c.status[0].toUpperCase() +
                      c.status.slice(1).toLowerCase()
                    : '-'
                }
              />
            </StyledRowContent>
          </StyledTableRow>,
        ],
        row: c.id,
      })),
    [displayedResults],
  );

  const { mutate: createCaseLinkMutation } = useCreateCaseLinkMutation({
    onSuccess: async () => {
      handleSuccessToast({
        toastMessage: 'Cases Linked successfully.',
      });
      refetchCaseLinks();
      await queryClient.invalidateQueries('CasesForLinking');
      setOpen(false);
      setLinkingLoading(false);
    },
    onError: () => {
      handleErrorToast({
        toastMessage: 'Error linking cases.',
      });
      setOpen(false);
      setLinkingLoading(false);
    },
  });

  const handleConfirm = () => {
    if (!selectedCase || !linkType) {
      setShowValidationError(true);
      return;
    }
    setLinkingLoading(true);
    createCaseLinkMutation({
      data: {
        sourceCaseId: caseId,
        targetCaseId: selectedCase,
        linkType: linkType.value as CaseLinkType,
        notes,
      },
    });
  };

  const handleClear = useCallback(() => {
    setSearchValue('');
    setSelectedCase(null);
    setLinkType(null);
    setNotes('');
    setShowValidationError(false);
    setOpen(false);
  }, [setOpen, setSearchValue]);

  return (
    <Modal
      size="medium"
      title={`Link Case to ${readableId}`}
      confirmButtonText="Link Case"
      includeClose
      open={open}
      setOpen={setOpen}
      confirmButtonIcon={{ position: 'left', className: 'uil uil-link' }}
      onCancel={handleClear}
      onConfirm={handleConfirm}
      data-testid="link-case-modal"
      callCancelOnOutsideClick
      loading={linkingLoading}
    >
      <FormInput
        label="Search for Case to link"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        variant={FormInputVariant.INPUT}
        state={
          showValidationError && !selectedCase
            ? FormState.ERROR
            : FormState.DEFAULT
        }
        isRequired
        helpText="Choosing a case to link is required. Please check and try again."
        placeholder="Search by case name or ID"
      />
      {hasSearchQuery ? (
        <TableContainer>
          <Table
            isFullWidth
            isVerticalScrollOnly
            columns={columns}
            tableData={tableData}
            maxRows={3}
            rowHeight={56}
            emptyText="No cases found"
            onRowClick={setSelectedCase}
            selectedRows={selectedCase ? [selectedCase] : []}
            allowRowSelect
            data-testid="link-case-table"
          />
          {showLoader && (
            <LoaderOverlay>
              <Center>
                <Spinner variant="secondary" size="large" />
              </Center>
            </LoaderOverlay>
          )}
        </TableContainer>
      ) : (
        <StyledEmptyStateContainer>
          <StyledIcon className="uil uil-search" />
          <div>Type a Case ID or Name to find cases</div>
        </StyledEmptyStateContainer>
      )}

      {selectedCase && (
        <LinkedCaseItemContainer>
          Selected Case
          <Button
            variant="secondary"
            size="medium"
            data-testid="case-linking-selected-case"
          >
            {getCaseReadableId(selectedCase)}
            <StyledIconInButton
              className="uil uil-multiply"
              size="small"
              position="right"
              onClick={() => setSelectedCase(null)}
            />
          </Button>
        </LinkedCaseItemContainer>
      )}

      <FormContainer>
        <FormInput
          label="Link type"
          options={linkTypeOptions}
          onChange={(value) => {
            setLinkType(value as DefaultOption | null);
          }}
          variant={FormInputVariant.DROPDOWN}
          value={linkType}
          placeholder="Select link type"
          state={
            showValidationError && !linkType
              ? FormState.ERROR
              : FormState.DEFAULT
          }
          helpText="Link type is required. Please select an option from the dropdown."
          isRequired
          data-testid="link-case-dropdown"
          description={
            linkType
              ? linkTypeDescriptions[linkType.value as CaseLinkType]
              : undefined
          }
        />
      </FormContainer>
      <FormInput
        label="Notes (Optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        variant={FormInputVariant.AREA}
        state={FormState.DEFAULT}
        placeholder="Add any additional context about these links..."
        data-testid="link-case-modal-notes"
      />
    </Modal>
  );
};
